import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RegistrationModal = ({ isOpen, onClose, eventTitle }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    studentId: '',
    program: '',
    year: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.name && formData.email) {
      try {
        // Send registration to backend
        const response = await axios.post(`${API}/register-event`, {
          event_title: eventTitle,
          name: formData.name,
          email: formData.email,
          studentId: formData.studentId,
          program: formData.program,
          year: formData.year
        });

        if (response.data) {
          setIsSubmitted(true);
          toast({
            title: "Registration Successful!",
            description: `You've registered for "${eventTitle}". Check your email for confirmation.`,
            variant: "default"
          });
          
          // Reset after 2 seconds and close modal
          setTimeout(() => {
            setFormData({ name: '', email: '', studentId: '', program: '', year: '' });
            setIsSubmitted(false);
            onClose();
          }, 2000);
        }
      } catch (error) {
        console.error('Registration error:', error);
        toast({
          title: "Registration Failed",
          description: "There was an error submitting your registration. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const handleClose = () => {
    if (!isSubmitted) {
      setFormData({ name: '', email: '', studentId: '', program: '', year: '' });
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        {!isSubmitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">
                Register for Event
              </DialogTitle>
              <DialogDescription className="text-slate-600">
                {eventTitle && `Join us for: ${eventTitle}`}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              <div>
                <Label htmlFor="name" className="text-slate-700 font-semibold">
                  Full Name *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                  className="mt-2 border-2 border-slate-200 focus:border-[#8B1538]"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-slate-700 font-semibold">
                  Email Address *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john.doe@uottawa.ca"
                  required
                  className="mt-2 border-2 border-slate-200 focus:border-[#8B1538]"
                />
              </div>

              <div>
                <Label htmlFor="studentId" className="text-slate-700 font-semibold">
                  Student ID
                </Label>
                <Input
                  id="studentId"
                  name="studentId"
                  type="text"
                  value={formData.studentId}
                  onChange={handleChange}
                  placeholder="300123456"
                  className="mt-2 border-2 border-slate-200 focus:border-[#8B1538]"
                />
              </div>

              <div>
                <Label htmlFor="program" className="text-slate-700 font-semibold">
                  Program
                </Label>
                <Select onValueChange={(value) => handleSelectChange('program', value)}>
                  <SelectTrigger className="mt-2 border-2 border-slate-200">
                    <SelectValue placeholder="Select your program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bcom">BCom - Commerce</SelectItem>
                    <SelectItem value="engineering">Engineering</SelectItem>
                    <SelectItem value="science">Science</SelectItem>
                    <SelectItem value="law">Law</SelectItem>
                    <SelectItem value="arts">Arts</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="year" className="text-slate-700 font-semibold">
                  Year of Study
                </Label>
                <Select onValueChange={(value) => handleSelectChange('year', value)}>
                  <SelectTrigger className="mt-2 border-2 border-slate-200">
                    <SelectValue placeholder="Select your year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1st Year</SelectItem>
                    <SelectItem value="2">2nd Year</SelectItem>
                    <SelectItem value="3">3rd Year</SelectItem>
                    <SelectItem value="4">4th Year</SelectItem>
                    <SelectItem value="graduate">Graduate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#8B1538] hover:bg-[#6B1028] text-white py-6 text-lg font-semibold"
              >
                Complete Registration
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">
              Registration Successful!
            </h3>
            <p className="text-slate-600">
              Check your email for confirmation details.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
